import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  getUsers,
  updateUser,
  deleteUser as apiDeleteUser,
  resendUserPasswordLink,
  getCompanies,
  getUserCompanyAccesses,
  updateUserCompanyAccesses,
} from "../../services/adminApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";
import { useAdminConfirmation } from "../../pages/admin/panel_engine/consumer";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";

import { AdminButton } from "../../pages/admin/action_engine";

import {
  AdminActions,
} from "../../pages/admin/section_engine";

const ROLE_OPTIONS = [
  { value: "ROLE_USER", label: "Utilisateur" },
  { value: "ROLE_ADMIN", label: "Administrateur" },
  { value: "ROLE_SUPER_ADMIN", label: "Super administrateur" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Homme" },
  { value: "female", label: "Femme" },
  { value: "other", label: "Autre" },
];

const CONTRACT_OPTIONS = [
  { value: "", label: "Non renseigné" },
  { value: "CDI", label: "CDI" },
  { value: "CDD", label: "CDD" },
  { value: "Alternance", label: "Alternance" },
  { value: "Stage", label: "Stage" },
  { value: "Extra", label: "Extra" },
];

function toDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export default function UserListPanel() {
  const [users, setUsers] = useState([]);
  const [editingUuid, setEditingUuid] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyAccesses, setCompanyAccesses] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();
  const confirm = useAdminConfirmation();

  const loadCompanies = async () => {
    try {
      const { data } = await getCompanies();
      setCompanies(data.companies || []);
    } catch (err) {
      logger.error(err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await getUsers({
        companyUuid: selectedCompanyUuid,
      });
      setUsers(data.users || []);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          "Erreur lors du chargement des utilisateurs."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUserCompanyAccesses = async (userUuid) => {
    setAccessLoading(true);
    setAccessError("");

    try {
      const { data } = await getUserCompanyAccesses(userUuid, {
        companyUuid: selectedCompanyUuid,
      });
      setCompanyAccesses(data.accesses || []);
    } catch (err) {
      logger.error(err);
      setCompanyAccesses([]);
      setAccessError(
        err?.response?.data?.error ||
          "Erreur lors du chargement des accès société."
      );
    } finally {
      setAccessLoading(false);
    }
  };

  const startEdit = (user) => {
    setEditingUuid(user.uuid);
    setAccessError("");
    loadUserCompanyAccesses(user.uuid);
    setDraft({
      username: user.username || "",
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      role: user.role || "user",
      email: user.email || "",
      phone_number: user.phone_number || "",
      gender: user.gender || "",
      address: user.address || "",
      postal_code: user.postal_code || "",
      city: user.city || "",
      country: user.country || "France",
      timezone: user.timezone || "Europe/Paris",
      social_security_number: user.social_security_number || "",
      date_of_birth: toDateInput(user.date_of_birth),
      employee_number: user.employee_number || "",
      company_uuid: user.company_uuid || "",
      job_title: user.job_title || "",
      department: user.department || "",
      hire_date: toDateInput(user.hire_date),
      termination_date: toDateInput(user.termination_date),
      contract_type: user.contract_type || "",
      profile_picture_url: user.profile_picture_url || "",
    });
    setMessage("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraft({});
    setCompanyAccesses([]);
    setAccessError("");
  };

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCompanyAccess = (company) => {
    setCompanyAccesses((prev) => {
      const exists = prev.find((access) => access.company_uuid === company.uuid);

      if (exists) {
        return prev.filter((access) => access.company_uuid !== company.uuid);
      }

      const next = [
        ...prev,
        {
          company_uuid: company.uuid,
          company_name: company.name,
          is_default: prev.length === 0,
          can_admin: false,
          can_access: true,
          is_active: true,
        },
      ];

      return next;
    });
  };

  const patchCompanyAccess = (companyUuid, patch) => {
    setCompanyAccesses((prev) =>
      prev.map((access) => {
        if (access.company_uuid !== companyUuid) {
          if (patch.is_default) {
            return { ...access, is_default: false };
          }
          return access;
        }

        return {
          ...access,
          ...patch,
        };
      })
    );
  };

  const saveUser = async (uuid) => {
    setMessage("");
    setError("");

    if (companyAccesses.length > 0) {
      const hasDefault = companyAccesses.some((access) => access.is_default);
      if (!hasDefault) {
        setError("Sélectionnez une société par défaut.");
        return;
      }
    }

    try {
      await updateUser(uuid, draft, {
        companyUuid: selectedCompanyUuid,
      });

      await updateUserCompanyAccesses(
        uuid,
        {
          accesses: companyAccesses.map((access) => ({
            company_uuid: access.company_uuid,
            is_default: Boolean(access.is_default),
            can_admin: Boolean(access.can_admin),
            can_access: Boolean(access.can_access),
            is_active: Boolean(access.is_active),
          })),
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setMessage("Utilisateur mis à jour.");
      setEditingUuid(null);
      setDraft({});
      setCompanyAccesses([]);
      setAccessError("");
      await loadUsers();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la mise à jour."
      );
    }
  };

  const deleteUser = async (uuid) => {
    const confirmed = await confirm({
      title: "Supprimer l’utilisateur ?",
      message: "Confirmer la suppression de cet utilisateur ?",
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    setMessage("");
    setError("");

    try {
      await apiDeleteUser(uuid, {
        companyUuid: selectedCompanyUuid,
      });
      setMessage("Utilisateur supprimé.");
      await loadUsers();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la suppression."
      );
    }
  };

  const resendPasswordLink = async (uuid) => {
    setMessage("");
    setError("");

    try {
      const { data } = await resendUserPasswordLink(uuid, {
        companyUuid: selectedCompanyUuid,
      });

      setMessage(
        data?.message || "Lien de définition du mot de passe envoyé."
      );
    } catch (err) {
      logger.error(err);

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        "Erreur lors de l'envoi du lien."
      );
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    setEditingUuid(null);
    setDraft({});
    setCompanyAccesses([]);
    setAccessError("");
    loadUsers();
  }, [selectedCompanyUuid]);

  if (loading) {
    return (
      <AdminLoadingState message="Chargement des utilisateurs..." />
    );
  }

  return (
    <UserListWrapper>
      {message && <PanelMessage>{message}</PanelMessage>}
      {error && (
        <AdminErrorState
          message={error}
          compact
        />
      )}

      {users.length === 0 ? (
        <AdminEmptyState message="Aucun utilisateur trouvé." />
      ) : (
        <UserCards>
          {users.map((user) => {
            const isEditing = editingUuid === user.uuid;

            return (
              <UserCard key={user.uuid}>
                <UserTopLine>
                  <div>
                    <strong>{user.username}</strong>
                    <small>{user.email}</small>
                  </div>
                  <RoleBadge>{user.role}</RoleBadge>
                </UserTopLine>

                {isEditing ? (
                  <EditGrid>
                    <SectionTitle>Identité</SectionTitle>

                    <Field>
                      <label>Nom d’utilisateur</label>
                      <input
                        name="username"
                        value={draft.username}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <TwoColumns>
                      <Field>
                        <label>Prénom</label>
                        <input
                          name="firstname"
                          value={draft.firstname}
                          onChange={handleDraftChange}
                        />
                      </Field>

                      <Field>
                        <label>Nom</label>
                        <input
                          name="lastname"
                          value={draft.lastname}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </TwoColumns>

                    <TwoColumns>
                      <Field>
                        <label>Genre</label>
                        <select
                          name="gender"
                          value={draft.gender}
                          onChange={handleDraftChange}
                        >
                          <option value="">Non renseigné</option>
                          {GENDER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field>
                        <label>Date de naissance</label>
                        <input
                          type="date"
                          name="date_of_birth"
                          value={draft.date_of_birth}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </TwoColumns>

                    <SectionTitle>Contact</SectionTitle>

                    <Field>
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={draft.email}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Téléphone</label>
                      <input
                        name="phone_number"
                        value={draft.phone_number}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Adresse</label>
                      <input
                        name="address"
                        value={draft.address}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <ThreeColumns>
                      <Field>
                        <label>Code postal</label>
                        <input
                          name="postal_code"
                          value={draft.postal_code}
                          onChange={handleDraftChange}
                        />
                      </Field>

                      <Field>
                        <label>Ville</label>
                        <input
                          name="city"
                          value={draft.city}
                          onChange={handleDraftChange}
                        />
                      </Field>

                      <Field>
                        <label>Pays</label>
                        <input
                          name="country"
                          value={draft.country}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </ThreeColumns>

                    <SectionTitle>Sociétés autorisées</SectionTitle>

                    {accessLoading ? (
                      <AdminLoadingState
                        message="Chargement des accès société..."
                        compact
                      />
                    ) : accessError ? (
                      <AdminErrorState
                        message={accessError}
                        actionLabel="Réessayer"
                        onAction={() =>
                          loadUserCompanyAccesses(editingUuid)
                        }
                        compact
                      />
                    ) : (
                      <CompanyAccessList>
                        {companies.map((company) => {
                          const access = companyAccesses.find(
                            (item) => item.company_uuid === company.uuid
                          );
                          const checked = Boolean(access);

                          return (
                            <CompanyAccessRow key={company.uuid} $active={checked}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCompanyAccess(company)}
                                />
                                <span>
                                  <strong>{company.name}</strong>
                                  <small>{company.uuid}</small>
                                </span>
                              </label>

                              {checked && (
                                <CompanyAccessOptions>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`default-company-${user.uuid}`}
                                      checked={Boolean(access.is_default)}
                                      onChange={() =>
                                        patchCompanyAccess(company.uuid, {
                                          is_default: true,
                                        })
                                      }
                                    />
                                    Défaut
                                  </label>

                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(access.can_admin)}
                                      onChange={(event) =>
                                        patchCompanyAccess(company.uuid, {
                                          can_admin: event.target.checked,
                                        })
                                      }
                                    />
                                    Admin
                                  </label>

                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(access.is_active)}
                                      onChange={(event) =>
                                        patchCompanyAccess(company.uuid, {
                                          is_active: event.target.checked,
                                        })
                                      }
                                    />
                                    Actif
                                  </label>
                                </CompanyAccessOptions>
                              )}
                            </CompanyAccessRow>
                          );
                        })}
                      </CompanyAccessList>
                    )}

                    <SectionTitle>Compte</SectionTitle>

                    <TwoColumns>
                      <Field>
                        <label>Rôle</label>
                        <select
                          name="role"
                          value={draft.role}
                          onChange={handleDraftChange}
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field>
                        <label>Fuseau horaire</label>
                        <input
                          name="timezone"
                          value={draft.timezone}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </TwoColumns>

                    <SectionTitle>RH / Contrat</SectionTitle>

                    <TwoColumns>
                      <Field>
                        <label>Matricule salarié</label>
                        <input
                          name="employee_number"
                          value={draft.employee_number}
                          onChange={handleDraftChange}
                          maxLength={8}
                        />
                      </Field>

                      <Field>
                        <label>Société principale</label>
                        <input
                          value="Définie automatiquement par la société par défaut ci-dessus"
                          disabled
                        />
                      </Field>

                      <Field>
                        <label>Type de contrat</label>
                        <select
                          name="contract_type"
                          value={draft.contract_type}
                          onChange={handleDraftChange}
                        >
                          {CONTRACT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </TwoColumns>

                    <TwoColumns>
                      <Field>
                        <label>Poste</label>
                        <input
                          name="job_title"
                          value={draft.job_title}
                          onChange={handleDraftChange}
                        />
                      </Field>

                      <Field>
                        <label>Département</label>
                        <input
                          name="department"
                          value={draft.department}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </TwoColumns>

                    <TwoColumns>
                      <Field>
                        <label>Date d’embauche</label>
                        <input
                          type="date"
                          name="hire_date"
                          value={draft.hire_date}
                          onChange={handleDraftChange}
                        />
                      </Field>

                      <Field>
                        <label>Date de sortie</label>
                        <input
                          type="date"
                          name="termination_date"
                          value={draft.termination_date}
                          onChange={handleDraftChange}
                        />
                      </Field>
                    </TwoColumns>

                    <Field>
                      <label>Numéro de sécurité sociale</label>
                      <input
                        name="social_security_number"
                        value={draft.social_security_number}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>URL photo de profil</label>
                      <input
                        name="profile_picture_url"
                        value={draft.profile_picture_url}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <AdminActions>
                      <AdminButton
                        variant="success"
                        type="button"
                        fullWidth
                        onClick={() => saveUser(user.uuid)}
                      >
                        Enregistrer
                      </AdminButton>
                      <AdminButton
                        variant="ghost"
                        type="button"
                        fullWidth
                        onClick={cancelEdit}
                      >
                        Annuler
                      </AdminButton>
                    </AdminActions>
                  </EditGrid>
                ) : (
                  <>
                    <UserMeta>
                      <span>
                        {user.firstname} {user.lastname}
                      </span>
                      <span>{user.phone_number || "Téléphone non renseigné"}</span>
                      <span>{user.city || "Ville non renseignée"}</span>
                      <span>{user.company_name || "Société non renseignée"}</span>
                      <span>{user.job_title || "Poste non renseigné"}</span>
                      <span>{user.contract_type || "Contrat non renseigné"}</span>
                    </UserMeta>

                    <UuidLine>{user.uuid}</UuidLine>

                    <AdminActions>
                      <AdminButton
                        variant="ghost"
                        type="button"
                        fullWidth
                        onClick={() => startEdit(user)}
                      >
                        Modifier
                      </AdminButton>

                      <AdminButton
                        variant="ghost"
                        type="button"
                        fullWidth
                        onClick={() => resendPasswordLink(user.uuid)}
                      >
                        Renvoyer lien MDP
                      </AdminButton>

                      <AdminButton
                        variant="danger"
                        type="button"
                        fullWidth
                        onClick={() => deleteUser(user.uuid)}
                      >
                        Supprimer
                      </AdminButton>
                    </AdminActions>
                  </>
                )}
              </UserCard>
            );
          })}
        </UserCards>
      )}
    </UserListWrapper>
  );
}

const UserListWrapper = styled.div`
  color: #f7f7f7;
`;

const UserCards = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const UserCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const UserTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  strong {
    display: block;
    font-weight: 950;
  }

  small {
    display: block;
    margin-top: 0.25rem;
    opacity: 0.7;
  }
`;

const RoleBadge = styled.span`
  height: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const UserMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.9rem;
  font-size: 0.9rem;
  opacity: 0.82;
`;

const UuidLine = styled.div`
  margin-top: 0.75rem;
  font-family: monospace;
  font-size: 0.72rem;
  opacity: 0.45;
  word-break: break-all;
`;

const EditGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const SectionTitle = styled.h4`
  margin: 1rem 0 0.25rem;
  font-size: 0.85rem;
  font-weight: 950;
  color: #fff9ea;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ThreeColumns = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1.2fr 1fr;
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.8;
  }

  input,
  select {
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(40, 140, 80, 0.9);
  font-weight: 850;
`;

const CompanyAccessList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const CompanyAccessRow = styled.div`
  border: 1px solid ${({ $active }) => ($active ? "#ff3561" : "rgba(11,19,41,0.12)")};
  border-radius: 14px;
  padding: 0.85rem;
  background: ${({ $active }) => ($active ? "rgba(255,53,97,0.06)" : "rgba(255,255,255,0.72)")};

  > label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
  }

  span {
    display: grid;
    gap: 0.15rem;
  }

  small {
    opacity: 0.62;
    font-size: 0.75rem;
  }
`;

const CompanyAccessOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 0.75rem;
  padding-left: 1.75rem;

  label {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.85rem;
  }
`;
